import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowLeft, Copy, Info, Layers3, Pencil, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import usePermissions from '../../../hooks/usePermissions';
import DialogForm from '../../../component/dialog/DialogForm';
import NewItemButton from '../../../component/common/NewItemButton';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../../component/table/DefaultTable';
import MobileCrudList from '../../../component/common/MobileCrud';
import { KitField, KitSelect } from '../../../component/common/MobileKitExtras';
import { protoIcon } from '../../../component/common/ProtoIcon';
import useIsMobileShell from '../../../hooks/useIsMobileShell';

const LayersIcon = protoIcon('layers');
const EditIcon = protoIcon('edit');
const CopyIcon = protoIcon('copy');
const TrashIcon = protoIcon('trash');

function removeAccents(str) {
    return (str || '').normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// Filtro aplicado só ao clicar "Buscar"/Enter (Uso em templates + Busca) —
// mesma técnica de `customers/List.js`/`suppliers/List.js` (report-toolbar
// §6.1): o estado do campo (`usageFilter`/`search`) é distinto do filtro
// efetivamente aplicado (`appliedUsage`/`appliedSearch`), que alimenta os
// chips removíveis. Ambos os predicados rodam 100% no cliente: `usage` é uma
// agregação calculada em `fetchData` (ver comentário lá), e a busca por
// nome/descrição sempre foi client-side de fato — o parâmetro `?filter=` que
// o código anterior mandava para `GET /checklist/groups` era ignorado pelo
// handler (`ListerInspGroup.ListInspGroup`, `fv-api/checklist/lister_insp_
// group.go`, nunca leu essa query string), então a busca nunca filtrou nada
// em produção; passar a filtrar de verdade no cliente é uma correção, não
// uma mudança de contrato de API.
function applyGroupFilters(list, usageValue, searchValue) {
    let result = Array.isArray(list) ? [...list] : [];

    if (usageValue === 'em-uso') {
        result = result.filter((g) => (g.usage || []).length > 0);
    } else if (usageValue === 'nao-utilizado') {
        result = result.filter((g) => (g.usage || []).length === 0);
    }

    if (searchValue && searchValue.trim()) {
        const term = removeAccents(searchValue.trim().toLowerCase());
        result = result.filter((g) => {
            const name = removeAccents((g.name || '').toLowerCase());
            const description = removeAccents((g.description || '').toLowerCase());
            return name.includes(term) || description.includes(term);
        });
    }

    return result;
}

// Pill "Em uso" — azul com contagem de templates + tooltip listando os
// nomes (DESIGN-SYSTEM.md §2, mesma anatomia de pill de `List.js`/
// `customers/List.js`), ou cinza "Não utilizado" quando `usage` está vazio.
function UsagePill({ usage }) {
    if (!usage || usage.length === 0) {
        return (
            <Box component="span" sx={{
                display: 'inline-flex', alignItems: 'center', fontSize: '11px', fontWeight: 700,
                padding: '4px 10px', borderRadius: '20px', bgcolor: 'background.subtle', color: 'text.disabled',
            }}>
                Não utilizado
            </Box>
        );
    }
    const label = `${usage.length} template${usage.length === 1 ? '' : 's'}`;
    return (
        <Tooltip title={usage.join(', ')} arrow placement="top">
            <Box component="span" sx={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700,
                padding: '4px 10px', borderRadius: '20px', bgcolor: 'info.bg', color: 'primary.main', cursor: 'default',
            }}>
                <Layers3 size={10} strokeWidth={2.5} />
                {label}
            </Box>
        </Tooltip>
    );
}

export default function ListInspGroup() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canCreateGroup = hasPermission('checklist.group.create');
    const canUpdateGroup = hasPermission('checklist.group.update');
    const canDeleteGroup = hasPermission('checklist.group.delete');

    const isMobileShell = useIsMobileShell();
    const [draftUsage, setDraftUsage] = useState('');
    const [groups, setGroups] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Valores dos campos do formulário de filtro (não aplicados ainda).
    const [usageFilter, setUsageFilter] = useState('');
    const [search, setSearch] = useState('');
    // Filtros efetivamente aplicados — alimentam os chips removíveis.
    const [appliedUsage, setAppliedUsage] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const handleShowSuccessSnackbar = (text) => {
        setSeverity('success');
        setMessage(text || 'Operação realizada com sucesso!');
        setOpenSnackbar(true);
    };

    const handleShowErrorSnackbar = (errorMessage) => {
        const text = typeof errorMessage === 'string'
            ? errorMessage
            : errorMessage != null
                ? JSON.stringify(errorMessage)
                : 'Erro ao realizar operação';
        setSeverity('error');
        setMessage(text);
        setOpenSnackbar(true);
    };

    // Catálogo de grupos + agregações "Itens"/"Em uso", só com endpoints já
    // existentes (nenhum endpoint/campo novo em fv-api):
    // 1) `GET /checklist/groups` — lista base (id/name/description).
    // 2) `GET /checklist` — lista de templates (id/name), já usada por
    //    `List.js`.
    // 3) `GET /checklist/{id}/view` por template — devolve a estrutura
    //    completa dos grupos anexados (mesmo endpoint que `ViewDialog.js` já
    //    consome), com `group_id` + `group_inspection_items`; cruzando por
    //    `group_id` obtemos tanto "em quais templates este grupo é usado"
    //    quanto a contagem de itens de qualquer grupo que apareça em pelo
    //    menos 1 template.
    // 4) `GET /checklist/group/{id}` — só para os grupos que não apareceram
    //    em nenhum template (mesmo endpoint que `GroupForm.js` usa para
    //    carregar um grupo para edição), para não deixar "Itens" em branco
    //    nos grupos ainda não utilizados.
    const fetchData = async () => {
        setLoading(true);
        try {
            const [groupsRes, templatesRes] = await Promise.all([
                httpClient.get('/checklist/groups'),
                httpClient.get('/checklist'),
            ]);
            const baseGroups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
            const templates = Array.isArray(templatesRes.data) ? templatesRes.data : [];

            const usageByGroupId = {};
            const itemsCountByGroupId = {};

            await Promise.all(templates.map(async (tpl) => {
                try {
                    const res = await httpClient.get(`/checklist/${tpl.id}/view`);
                    const groupsInTpl = Array.isArray(res.data) ? res.data : [];
                    groupsInTpl.forEach((g) => {
                        if (!g.group_id) return;
                        if (!usageByGroupId[g.group_id]) usageByGroupId[g.group_id] = [];
                        usageByGroupId[g.group_id].push(tpl.name);
                        if (itemsCountByGroupId[g.group_id] == null) {
                            const items = Array.isArray(g.group_inspection_items)
                                ? g.group_inspection_items.filter((i) => i.active !== false)
                                : [];
                            itemsCountByGroupId[g.group_id] = items.length;
                        }
                    });
                } catch (error) {
                    console.error(`Erro ao carregar grupos do template ${tpl.id}:`, error);
                }
            }));

            await Promise.all(baseGroups.map(async (g) => {
                if (itemsCountByGroupId[g.id] != null) return;
                try {
                    const res = await httpClient.get(`/checklist/group/${g.id}`);
                    const items = Array.isArray(res.data?.group_inspection_items)
                        ? res.data.group_inspection_items.filter((i) => i.active !== false)
                        : [];
                    itemsCountByGroupId[g.id] = items.length;
                } catch (error) {
                    console.error(`Erro ao carregar itens do grupo ${g.id}:`, error);
                    itemsCountByGroupId[g.id] = 0;
                }
            }));

            const enriched = baseGroups.map((g) => ({
                ...g,
                itemsCount: itemsCountByGroupId[g.id] ?? 0,
                usage: usageByGroupId[g.id] || [],
            }));

            setGroups(enriched);
            setFilteredData(applyGroupFilters(enriched, appliedUsage, appliedSearch));
        } catch (error) {
            console.error('Erro ao carregar grupos de inspeção:', error);
            setGroups([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApplyFilters = () => {
        setAppliedUsage(usageFilter);
        setAppliedSearch(search);
        setFilteredData(applyGroupFilters(groups, usageFilter, search));
    };

    const handleClearFilters = () => {
        setUsageFilter('');
        setSearch('');
        setAppliedUsage('');
        setAppliedSearch('');
        setFilteredData(groups);
    };

    const handleRemoveUsageFilter = () => {
        setUsageFilter('');
        setAppliedUsage('');
        setFilteredData(applyGroupFilters(groups, '', appliedSearch));
    };

    const handleRemoveSearchFilter = () => {
        setSearch('');
        setAppliedSearch('');
        setFilteredData(applyGroupFilters(groups, appliedUsage, ''));
    };

    const handleOpenNew = () => {
        navigate('/checklist/group/add');
    };

    // "Editar" (ícone da linha e clique na linha inteira) navega para o
    // editor de grupo em modo avulso — rota `/checklist/group/edt?groupId=`,
    // já registrada em `DefaultRouter.js` e resolvida por `GroupForm.js`
    // (`StandaloneGroupForm`) sem nenhum `tplId` (edição fora do contexto de
    // um template específico), voltando para esta própria lista ao salvar.
    const handleOpenEdit = (row) => {
        navigate(`/checklist/group/edt?groupId=${row.id}`);
    };

    // "Duplicar" usa o endpoint dedicado `POST /checklist/group/{id}/duplicate`,
    // que exige apenas `checklist.group.create` (o backend clona nome, descrição,
    // respostas e itens e gera o nome com o sufixo " - duplicado").
    const handleDuplicateGroup = async (row) => {
        setLoading(true);
        try {
            const response = await httpClient.post(`/checklist/group/${row.id}/duplicate`);
            if (!response.data?.id) {
                handleShowErrorSnackbar('Erro ao duplicar grupo de inspeção');
                return;
            }
            await fetchData();
            handleShowSuccessSnackbar(`Grupo "${row.name}" duplicado — a cópia ainda não está em uso em nenhum template.`);
        } catch (error) {
            console.error('Erro ao duplicar grupo de inspeção:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao duplicar grupo de inspeção');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGroup = async () => {
        setDeleting(true);
        try {
            const response = await httpClient.delete(`/checklist/group/${groupToDelete}`);
            if (response.status === 204 || response.status === 200) {
                setOpenDialogConfirmDelete(false);
                setGroupToDelete(null);
                await fetchData();
                handleShowSuccessSnackbar('Grupo de inspeção excluído com sucesso!');
            }
        } catch (error) {
            console.error('Erro ao excluir grupo de inspeção:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao excluir grupo de inspeção');
        } finally {
            setDeleting(false);
        }
    };

    const groupToDeleteName = groups.find((g) => g.id === groupToDelete)?.name || '';

    const columns = [
        {
            name: 'Nome',
            path: 'name',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }} title={row.name}>
                    {row.name}
                </Typography>
            ),
        },
        {
            name: 'Descrição',
            path: 'description',
            render: (row) => (
                <Box sx={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320,
                    fontSize: '13px', color: 'text.secondary',
                }} title={row.description}>
                    {row.description || 'Sem descrição'}
                </Box>
            ),
        },
        {
            name: 'Itens',
            path: 'itemsCount',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.itemsCount} ite{row.itemsCount === 1 ? 'm' : 'ns'}
                </Typography>
            ),
        },
        {
            name: 'Em uso',
            path: 'usage',
            render: (row) => <UsagePill usage={row.usage} />,
        },
    ];

    // "Excluir" fica bloqueado (tooltip fixo "Excluir" + toast explicando a
    // regra ao clicar) quando o grupo está em uso em 1+ templates — mesmo
    // tratamento dado ao template padrão em `List.js` (`TableIconAction` não
    // suporta tooltip dinâmico por linha nem `disabled` por linha; o ícone
    // muda de cor e o clique explica a regra via snackbar em vez de abrir a
    // confirmação, evitando modificar o componente compartilhado).
    const actions = [
        ...(canUpdateGroup ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: { onClick: handleOpenEdit, tooltip: 'Editar' },
        }] : []),
        ...(canCreateGroup ? [{
            icon: <Copy size={16} strokeWidth={2} />,
            props: { onClick: handleDuplicateGroup, tooltip: 'Duplicar' },
        }] : []),
        ...(canDeleteGroup ? [{
            icon: (row) => <Trash2 size={16} strokeWidth={2} color={(row.usage || []).length > 0 ? theme.palette.text.disabled : theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    const usage = row.usage || [];
                    if (usage.length > 0) {
                        handleShowErrorSnackbar(`Não é possível excluir "${row.name}" — ele está em uso em ${usage.length} template(s) (${usage.join(', ')}). Remova-o desses templates antes de excluir.`);
                        return;
                    }
                    setGroupToDelete(row.id);
                    setOpenDialogConfirmDelete(true);
                },
                tooltip: 'Excluir',
            },
        }] : []),
    ];

    // Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), mesmo padrão
    // de `List.js`/`categories/List.js`/`services/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const usageFilterLabel = usageFilter === 'em-uso' ? 'Em uso' : usageFilter === 'nao-utilizado' ? 'Não utilizado' : 'Todos';
    const appliedUsageLabel = appliedUsage === 'em-uso' ? 'Em uso' : 'Não utilizado';
    const unusedCount = groups.filter((g) => (g.usage || []).length === 0).length;

    const requestDeleteGroup = (row) => {
        const usage = row.usage || [];
        if (usage.length > 0) {
            handleShowErrorSnackbar(`Não é possível excluir "${row.name}" — ele está em uso em ${usage.length} template(s) (${usage.join(', ')}). Remova-o desses templates antes de excluir.`);
            return;
        }
        setGroupToDelete(row.id);
        setOpenDialogConfirmDelete(true);
    };

    // Shell mobile: lista de `checklists/grupos.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget={'Grupos de inspeção'} message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
                <MobileCrudList
                    items={applyGroupFilters(groups, appliedUsage, search)}
                    getKey={(row) => row.id}
                    getRow={(row) => ({
                        icon: LayersIcon,
                        title: row.name,
                        sub: `${row.itemsCount} ite${row.itemsCount === 1 ? 'm' : 'ns'}`,
                        chevron: true,
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Buscar grupo…"
                    filter={{
                        active: Boolean(appliedUsage),
                        onOpen: () => setDraftUsage(appliedUsage),
                        onApply: () => setAppliedUsage(draftUsage),
                        onClear: handleClearFilters,
                        children: (
                            <KitField label="Uso em templates">
                                <KitSelect value={draftUsage} onChange={(e) => setDraftUsage(e.target.value)} aria-label="Uso em templates">
                                    <option value="">Todos</option>
                                    <option value="em-uso">Em uso</option>
                                    <option value="nao-utilizado">Não utilizado</option>
                                </KitSelect>
                            </KitField>
                        ),
                    }}
                    emptyIcon={LayersIcon}
                    emptyTitle="Nenhum grupo encontrado"
                    emptyText="Ajuste a busca ou os filtros para ver outros grupos."
                    fabLabel={canCreateGroup ? 'Novo' : undefined}
                    onNew={handleOpenNew}
                    rowTitle={(row) => row.name}
                    rowActions={(row) => [
                        ...(canUpdateGroup ? [{ label: 'Editar', icon: EditIcon, onClick: () => handleOpenEdit(row) }] : []),
                        ...(canCreateGroup ? [{ label: 'Duplicar', icon: CopyIcon, onClick: () => handleDuplicateGroup(row) }] : []),
                        ...(canDeleteGroup ? [{ label: 'Excluir', icon: TrashIcon, destructive: true, onClick: () => requestDeleteGroup(row) }] : []),
                    ]}
                />
                <DialogForm
                    iconType={'warning'}
                    open={openDialogConfirmDelete}
                    title={'Atenção'}
                    confirmButtonText={'Sim, Excluir'}
                    content={`Tem certeza que deseja excluir o grupo "${groupToDeleteName}"?`}
                    secondaryContent={'Esta ação não pode ser desfeita. O grupo será removido permanentemente do sistema.'}
                    onSubmit={handleDeleteGroup}
                    onClose={() => {
                        setOpenDialogConfirmDelete(false);
                        setGroupToDelete(null);
                    }}
                    loading={deleting}
                    loadingText="Removendo..."
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Grupos de Inspeção'} message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ação "Novo grupo". */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton
                            onClick={() => navigate('/vehicle_checklist')}
                            title="Voltar"
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', flexShrink: 0 }}
                        >
                            <ArrowLeft size={18} strokeWidth={2} />
                        </IconButton>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', background: (t) => t.palette.gradient.brand,
                        }}>
                            <Layers3 size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Grupos de Inspeção
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Catálogo reutilizável de grupos de inspeção — cada grupo pode ser anexado a vários templates de checklist ao mesmo tempo.
                            </Typography>
                        </Box>
                    </Box>

                    {canCreateGroup && (
                        <NewItemButton label="Novo grupo" onClick={handleOpenNew} />
                    )}
                </Box>

                {/* report-toolbar — Uso em templates/Buscar. */}
                <Card sx={{ mb: 2 }}>
                    <CardContent sx={{
                        p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2,
                    }}>
                        <Box>
                            <TbLabel>Uso em templates</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <Select
                                    value={usageFilter}
                                    displayEmpty
                                    onChange={(e) => setUsageFilter(e.target.value)}
                                    renderValue={() => usageFilterLabel}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="em-uso">Em uso</MenuItem>
                                    <MenuItem value="nao-utilizado">Não utilizado</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '0 0 260px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome ou descrição do grupo…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleApplyFilters();
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 auto' }} />

                        <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ height: '33px' }}>
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Search size={14} strokeWidth={2} />}
                            onClick={handleApplyFilters}
                            sx={{ height: '33px' }}
                        >
                            Buscar
                        </Button>
                    </CardContent>
                </Card>

                {/* chips-row — filtros ativos removíveis + contagem/"grupos não utilizados". */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                    {appliedUsage && (
                        <Chip
                            label={`Uso: ${appliedUsageLabel}`}
                            size="small"
                            onDelete={handleRemoveUsageFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={handleRemoveSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${filteredData.length} grupo${filteredData.length === 1 ? '' : 's'} encontrado${filteredData.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`${unusedCount} grupo${unusedCount === 1 ? '' : 's'} não utilizado${unusedCount === 1 ? '' : 's'} em nenhum template`}
                        size="small"
                        sx={unusedCount > 0 ? { bgcolor: 'warning.bg', color: 'warning.main' } : { bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </Stack>

                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    onRowClick={canUpdateGroup ? handleOpenEdit : undefined}
                    loading={loading}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="grupos"
                    sortableFields={['name', 'description']}
                    defaultSort={{ field: 'name', direction: 'asc' }}
                />

                {/* Nota de rodapé — deixa explícito onde este catálogo é
                    consumido (combobox "Adicionar grupo existente" do editor
                    de template) e que "Editar"/"Novo grupo" abrem o mesmo
                    editor de estrutura de grupo usado a partir de um
                    template, só que em modo avulso. */}
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', mt: 2, p: '12px 14px', borderRadius: '12px',
                    bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider',
                }}>
                    <Info size={16} strokeWidth={2} color={theme.palette.text.disabled} style={{ marginTop: 1, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5 }}>
                        <Box component="span" sx={{ display: 'block', fontWeight: 700, color: 'text.primary', fontSize: '12.5px', mb: '2px' }}>
                            Catálogo reutilizável
                        </Box>
                        Estes grupos alimentam o combobox "Adicionar grupo existente" no{' '}
                        <Box
                            component="span"
                            onClick={() => navigate('/vehicle_checklist')}
                            sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            editor de template de checklist
                        </Box>{' '}
                        — o mesmo grupo pode estar anexado a vários templates ao mesmo tempo, e editar um grupo (aqui ou por lá) afeta todos os templates que o utilizam. "Editar"/"Novo grupo" abrem o editor de estrutura do grupo (itens de inspeção e respostas OK/NOK/Manutenção).
                    </Typography>
                </Box>
            </Box>

            <DialogForm
                iconType={'warning'}
                open={openDialogConfirmDelete}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={`Tem certeza que deseja excluir o grupo "${groupToDeleteName}"?`}
                secondaryContent={'Esta ação não pode ser desfeita. O grupo será removido permanentemente do sistema.'}
                onSubmit={handleDeleteGroup}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setGroupToDelete(null);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />
        </PageScaffold>
    );
}
